import { creatureNameTables } from '../advancement/random-creature-name';
import { itemsCol, layerOf, layerEntriesOf, layerListEntryUuid, resolveNest, inventoryField } from '../../shared/array-types';
import { mathRandomDie, type DieSource } from '../rolls/dice-roll';
import { userChoiceOptions } from '../display/user-choice-selector';
import { layerHas } from '../../shared/array-types';
import { inventoryListEntry } from '../actions/ability-preconditions';

export type PanelRow = Record<string, any>;
export const creatorId = (value: unknown): string => (typeof value === 'string' ? value.split('|')[0]! : layerListEntryUuid(value)).trim().toUpperCase();
export function creatorNode(value: unknown, root: unknown): PanelRow {
    const id = creatorId(value), resolved = resolveNest(typeof value === 'string' ? id : value, root);
    if (resolved && typeof resolved === 'object' && resolved.item_layer_types && itemsCol(resolved, 'item_id')) return resolved;
    // resolveNest indexes full layer-bearing nodes. A valid lean/zero-layer definition, or a
    // definition with wrapped identity, can still be held explicitly in the native catalog.
    const catalog = root && typeof root === 'object' ? (root as PanelRow).catalog : null;
    const known = catalog?.[id] ?? catalog?.[id.toLowerCase()];
    return known && typeof known === 'object' && itemsCol(known, 'item_id') ? known : resolved ?? {};
}
export const creatorEssences = ['body', 'mind', 'soul', 'spirit', 'fortune'] as const;

/** Creator and read cards use the same browser selection predicates. */
export function creatorSpellOptions(layer: string, spec: string, catalog: PanelRow[]): ReturnType<typeof userChoiceOptions> {
    return userChoiceOptions(layer, spec, catalog, {
        value: layerOf, entries: layerEntriesOf, has: layerHas, field: itemsCol, uuid: creatorId,
    });
}

/** Keep provider order. Level-less passives belong only to the first ancestry cell. */
export function creatorAncestryPassives(ancestry: PanelRow, level: number): PanelRow[] {
    return layerEntriesOf(ancestry, 'passive_ability').map(entry => {
        const node = creatorNode(entry, ancestry);
        if (!itemsCol(node, 'item_id')) throw new Error(`unported:creator-panels:missing-passive-definition:${creatorId(entry)}`);
        return node;
    }).filter(node => {
        const required = layerOf(node, 'requires_level');
        return required == null || required === '' ? level === 1 : Number(required) === level;
    });
}

export function creatorEssenceValues(node: unknown): Record<string, string> {
    return Object.fromEntries(creatorEssences.flatMap(essence => {
        const value = String(layerOf(node, `ancestral_${essence}`) ?? '').replace('+', '').trim();
        return value !== '' && Number.isFinite(Number(value)) ? [[`ancestral_${essence}`, value]] : [];
    }));
}

export function creatorFeatAllowed(feat: unknown, state: PanelRow, tiers: PanelRow[]): boolean {
    const matches = (key: string, choices: unknown[]): boolean => {
        const required = layerEntriesOf(feat, key).map(creatorId);
        return !required.length || choices.some(value => required.includes(creatorId(value)));
    };
    if (!matches('requires_ancestry', [state.ancestry_1, state.ancestry_2]) || !matches('requires_lineage', [state.lineage])
        || !matches('requires_class', Object.entries(state).filter(([key]) => /^class_\d+$/.test(key)).map(([, value]) => value))) return false;
    for (const essence of creatorEssences) {
        const minimum = layerOf(feat, `requires_min_essence_${essence}`);
        if (minimum != null && Number(state[essence] ?? 0) < Number(minimum)) return false;
    }
    return tiers.every(tier => layerEntriesOf(feat, String(tier.requires_skill_tier)).every(skill =>
        Number(state[creatorId(skill)] ?? state[creatorId(skill).toLowerCase()] ?? 0) >= Number(tier.skill_tier_level)));
}

export function creatorSavedChoice(rows: PanelRow[], layer: string, owner: string, source: string): string {
    const sameFamily = (row: PanelRow): boolean => String(itemsCol(row, 'choice_type') ?? '').replace(/^user_choice_\d+_/, '') === layer.replace(/^user_choice_\d+_/, '');
    const pair = (row: PanelRow, a: string, b: string): boolean => creatorId(itemsCol(row, 'choice_item_id')) === creatorId(a) && creatorId(itemsCol(row, 'exact_choice_item_id')) === creatorId(b);
    const found = rows.find(row => itemsCol(row, 'choice_type') === layer && pair(row, owner, source))
        ?? rows.find(row => sameFamily(row) && pair(row, owner, source))
        ?? (source ? rows.find(row => sameFamily(row) && pair(row, source, owner))
            ?? rows.find(row => sameFamily(row) && creatorId(itemsCol(row, 'exact_choice_item_id')) === creatorId(source)) : undefined);
    return String(itemsCol(found, 'choice') ?? '');
}

export function creatorSkillGrants(node: unknown, tiers: readonly PanelRow[]): Record<string, number> {
    const grants: Record<string, number> = {};
    for (const tier of tiers) for (const skill of layerEntriesOf(node, String(tier.variable_name))) {
        const id = creatorId(skill);
        if (id) grants[id] = Math.max(grants[id] ?? 0, Number(tier.skill_tier_level));
    }
    return grants;
}

const worn = new Set(['armor', 'natural_armor', 'clothing', 'cloak', 'head', 'feet', 'legs', 'bracers', 'breast', 'neck', 'ring', 'tail', 'air_around']);
const wornMounts: Record<string, string> = { head: 'worn_head', clothing: 'worn_torso', armor: 'worn_torso', natural_armor: 'worn_torso',
    breast: 'worn_torso', bracers: 'worn_hands', legs: 'worn_legs', feet: 'worn_feet', cloak: 'worn_cloak', neck: 'worn_neck' };
export function creatorDefaultMount(item: unknown, slot: string): string {
    const type = String(itemsCol(item, 'item_type') ?? '');
    const hay = [itemsCol(item, 'item_name'), ...['armor_classification', 'weapon_classification', 'weapon_type', 'armor_type', 'weapon_category']
        .flatMap(key => layerEntriesOf(item, key).map(value => itemsCol(creatorNode(value, item), 'item_name')))].join(' ').toLowerCase();
    if (/\b(shields?|buckler)\b/.test(hay)) return 'back_shield';
    if (/\b(bows?|\w*(short|long|cross|great|war|recurve)bows?)\b/.test(hay)) return 'back_bow';
    if (/\bquivers?\b/.test(hay)) return 'back_quiver';
    if (wornMounts[slot]) return wornMounts[slot]!;
    if (worn.has(slot)) return 'worn';
    if (['armor', 'clothing'].includes(type)) return 'worn_torso';
    if (type === 'instrument' || /backpack|bedroll/.test(hay)) return 'back_pack';
    if (type === 'weapon' || ['both_hands', 'main_hand', 'off_hand'].includes(slot)) return slot === 'both_hands' ? 'back_primary' : 'hip_auto';
    return slot === 'waist' || type === 'container' ? 'waist' : 'hidden';
}

export interface CreatorGearMetadata { slots: PanelRow[]; mounts: PanelRow[]; belt_ids: string[]; missingDefinitionIds?: string[] }
const variableOf = (registry: PanelRow[], id: unknown): string => String(registry.find(row => creatorId(row.item_id) === creatorId(id))?.variable_name ?? '');
const registryId = (registry: PanelRow[], variable: string): string => String(registry.find(row => row.variable_name === variable)?.item_id ?? '');
export function creatorEquipmentPresentation(item: unknown, equipped: unknown, metadata: CreatorGearMetadata): { slot: string; mount: string; itemType: string } {
    const slot = variableOf(metadata.slots, equipped);
    return { slot, mount: variableOf(metadata.mounts, layerOf(item, 'equipment_visual_mount')) || creatorDefaultMount(item, slot),
        itemType: String(itemsCol(item, 'item_type') ?? '') };
}

/** Creator-only loadout. Definitions are already compiled by the page's shared runtime. */
export function creatorStartingGear(owner: PanelRow, metadata: CreatorGearMetadata, occupied: PanelRow, newId: () => string): PanelRow[] {
    const slots = { ...(occupied.preoccupied_slots ?? {}) }, mounts = { ...(occupied.preoccupied_mounts ?? {}) };
    const sources = ['starting_equipment_item_id_equipped', 'starting_equipment_item_id_unequipped'];
    if (!sources.some(key => layerEntriesOf(owner, key).length)) sources.push('starting_equipment_item_id');
    const candidates = sources.flatMap(key => layerEntriesOf(owner, key).flatMap((entry, index) => {
        const definition = creatorNode(entry, owner), item = { ...definition, catalog: definition.catalog ?? owner.catalog };
        const id = creatorId(entry), name = String(itemsCol(item, 'item_name') ?? '');
        // Saved tombstones and explicit missing references omit optional gear; an incomplete read still refuses.
        if (Number(itemsCol(item, 'item_deleted') ?? 0)
            || metadata.missingDefinitionIds?.map(creatorId).includes(id)) return [];
        if (!itemsCol(item, 'item_id')) throw new Error(`unported:creator-panels:missing-gear-definition:${id}`);
        const spec = inventoryListEntry(typeof entry === 'string' ? entry : { ...entry,
            template_id: itemsCol(entry, 'template_id'),
            qty: itemsCol(entry, 'qty') ?? inventoryField(entry, 'item_qty') ?? itemsCol(entry, 'item_qty') });
        const template = creatorId(spec.template ?? '');
        const slot = variableOf(metadata.slots, layerOf(item, 'equipment_slot'));
        const mount = variableOf(metadata.mounts, layerOf(item, 'equipment_visual_mount')) || creatorDefaultMount(item, slot);
        const qty = spec.qty == null ? 1 : typeof entry === 'string' ? Math.round(spec.qty) : spec.qty;
        if (!Number.isFinite(qty) || qty < 0) throw new Error('unported:creator-panels:invalid-gear-quantity');
        const priority = worn.has(slot) ? 0 : slot === 'both_hands' ? 1 : mount === 'back_shield' ? 2 : ['main_hand', 'off_hand'].includes(slot) ? 3 : 4;
        return [{ item, id, name, slot, mount, qty, template, key, priority, index, equipped: key.endsWith('_equipped'), granted: '', parent: '', contained: '', follows: -1, mode: 'full', inventoryId: newId() }];
    })).sort((a, b) => Number(b.equipped) - Number(a.equipped) || (a.equipped ? a.priority - b.priority
        || Number(layerOf(b.item, 'cost_gp') ?? 0) - Number(layerOf(a.item, 'cost_gp') ?? 0) : 0));
    const free = (slot: string): boolean => slot === 'both_hands' ? !slots.both_hands && !slots.main_hand && !slots.off_hand
        : !(['main_hand', 'off_hand'].includes(slot) && slots.both_hands) && Number(slots[slot] ?? 0) < Number(metadata.slots.find(row => row.variable_name === slot)?.slot_limit ?? 1);
    let belt = false;
    for (const row of candidates) {
        const isBelt = metadata.belt_ids.map(creatorId).includes(row.id);
        if (!row.equipped || !row.slot || (row.slot === 'waist' && belt && isBelt)) continue;
        row.granted = free(row.slot) ? row.slot : row.slot === 'main_hand' && free('off_hand') ? 'off_hand'
            : row.slot === 'off_hand' && free('main_hand') ? 'main_hand' : '';
        if (row.granted) { slots[row.granted] = Number(slots[row.granted] ?? 0) + 1; if (row.granted === 'waist' && isBelt) belt = true; }
    }
    const family = (name: string, container: boolean): string => container ? name === 'sheath, dagger' ? 'dagger' : name === 'sheath, sword' ? 'sword' : ''
        : name === 'dagger' ? 'dagger' : name.endsWith('sword') ? 'sword' : '';
    const sheaths: Record<string, number[]> = {};
    candidates.forEach((row, index) => {
        const key = family(row.name.toLowerCase(), true);
        if (itemsCol(row.item, 'item_type') === 'container' && key) (sheaths[key] ??= []).push(index);
    });
    for (const equipped of [false, true]) candidates.forEach((row, index) => {
        if (itemsCol(row.item, 'item_type') !== 'weapon' || !!row.granted !== equipped) return;
        const key = family(row.name.toLowerCase(), false), sheathIndex = sheaths[key]?.shift();
        if (sheathIndex == null) return;
        const sheath = candidates[sheathIndex]!; sheath.mount = row.mount;
        if (equipped) sheath.follows = index;
        else { row.parent = sheath.inventoryId; row.mode = 'hilt_only'; sheath.contained = row.inventoryId; }
    });
    const placed: string[] = [];
    const mountFree = (key: string): boolean => Number(mounts[key] ?? 0) < Number(metadata.mounts.find(row => row.variable_name === key)?.mount_limit ?? 1);
    for (const row of candidates) {
        let mount = row.mount;
        if ((mount === 'worn' || mount.startsWith('worn_')) && !row.granted) mount = 'hidden';
        if (row.parent) mount = 'hidden';
        else if (row.follows >= 0) mount = placed[row.follows] ?? 'hidden';
        else {
            if (mount === 'hip_auto') mount = ['hip_left', 'hip_right', 'thigh_left', 'thigh_right'].find(mountFree) ?? 'hidden';
            else if (!(mount === 'hidden' || mount === 'worn' || mount.startsWith('worn_')) && !mountFree(mount)) {
                const other: Record<string, string> = { hip_left: 'hip_right', hip_right: 'hip_left', thigh_left: 'thigh_right', thigh_right: 'thigh_left' };
                mount = other[mount] && mountFree(other[mount]!) ? other[mount]! : 'hidden';
            }
            mounts[mount] = Number(mounts[mount] ?? 0) + 1;
        }
        placed.push(mount); row.mount = mount;
    }
    for (const row of candidates) if (row.parent) row.mount = candidates.find(parent => parent.inventoryId === row.parent)?.mount ?? 'hidden';
    return candidates.sort((a, b) => Number(!!b.contained) - Number(!!a.contained)).map(row => ({
        ...row.item, catalog: owner.catalog, inventory: { inventory_id: row.inventoryId, inventory_item_id: row.id, inventory_name: row.name,
            item_qty: row.qty, possess_id: row.parent, equipped: registryId(metadata.slots, row.granted), enchantment_template_id: row.template,
            equipment_slot_variable: row.granted, visual_mount_variable: row.mount, visual_mount: registryId(metadata.mounts, row.mount),
            visual_render_mode: row.mode, starting_equipment_source_layer: row.key, starting_equipment_requested: row.equipped ? 'equipped' : 'carried' },
    }));
}

export function creatorMeasurement(ancestry: PanelRow, kind: 'height' | 'weight' | 'age', draw: DieSource = mathRandomDie): string {
    const passive = creatorAncestryPassives(ancestry, 1).find(node => Number(layerOf(node, 'requires_level')) === 1);
    if (!passive) return kind === 'height' ? `5' 8"` : kind === 'weight' ? '150' : '25';
    const roll = (diceKey: string, dieKey: string): number => {
        const dice = Number(layerOf(passive, diceKey) ?? 0), die = Number(layerOf(passive, dieKey) ?? 0);
        if (!Number.isFinite(dice) || !Number.isFinite(die) || dice < 0 || dice > 1000 || (dice > 0 && die < 1)) throw new Error('unported:creator-panels:invalid-measurement');
        return Array.from({ length: dice }, () => draw(die)).reduce((sum, value) => sum + value, 0);
    };
    if (kind === 'age') return String(Number(layerOf(passive, 'age_young') ?? 5) + draw(20));
    const heightMod = roll('base_height_in_mod_dice', 'base_height_in_mod_die');
    if (kind === 'height') { const inches = Number(layerOf(passive, 'base_height_in') ?? 68) + heightMod; return `${Math.floor(inches / 12)}' ${inches % 12}"`; }
    return String(Number(layerOf(passive, 'base_weight_lbs') ?? 150) + heightMod * roll('base_weight_lbs_mod_dice', 'base_weight_lbs_mod_die'));
}

// Compatibility name-table identities from the creator's former ancestry buckets. Authored
// name references on the ancestry/Origin take precedence; table rows still come from SELECT.
const ancestryNameFamilies: Record<string, string> = {
    "CF1C7A6E-BCC2-954C-3B10-CD3622D10F24": "dragonfolk",
    "C9966EFA-4C55-C62A-186B-C85649F07B1C": "dragonfolk",
    "D4ECC0A0-27A7-DC15-C56D-20228F0640A9": "dragonfolk",
    "A64BD141-8275-28F6-8471-7A2F9AF295C4": "dragonfolk",
    "A44D9EFA-554A-6D87-480F-85138AB25E02": "dragonfolk",
    "CAAA63F4-A91D-37CA-A42B-8800BD981C49": "dragonfolk",
    "BA57B825-F810-3D77-A15A-21C946C2DE29": "dragonfolk",
    "F2F627F2-ABB3-3472-C9F3-C5ACAB1BF99E": "dragonfolk",
    "CBEEE0B5-3190-F2A5-76F2-45080AF27F4C": "dragonfolk",
    "8D6FAE28-8A50-BA8A-1B7F-44CD789B6F0A": "dragonfolk",
    "6A45CB9D-09EF-A1E0-A603-812A1626F9B9": "dwarf",
    "8C2FB0B9-CB95-21F9-A9BE-AD29640BE793": "dwarf",
    "C58E26B5-547A-081E-3E76-B41CD335E5FA": "elf",
    "D1BD9BFA-43CE-D615-14DA-4B3098F47D32": "elf",
    "CA2B1256-DB18-B51D-4CF3-C484B3029041": "elf",
    "1892EF16-671A-4A8C-94EC-A637B2C68597": "gnome",
    "F8D14A90-5502-65ED-AFAD-9EDE6BEDB5C1": "gnome",
    "A8A74B03-560C-13E3-2080-1222C656AC20": "gnome",
    "063B61EE-DE01-CCEA-92F7-334F6B725EFD": "gnome",
    "3C04CE74-D04D-4BF5-D7BF-78C124DCD11C": "gnome",
    "31408BCB-184D-A014-E91B-7683EABAD67C": "gnome",
    "68B9E080-C256-71E3-260A-4E30BDE23613": "gnome",
    "5F7CF41E-D91F-9DEC-69C7-587DD015A44B": "halfolk",
    "7CE7F0BD-600D-3E84-140F-A0257B4C5041": "human",
    "0412241B-FC60-4414-CDE9-2F8AEEF18D63": "orc",
    "224ED429-207C-6830-19FD-58223C8B5852": "leprechaun",
    "700A3B33-9A51-DC1D-B3DA-D9DE62BB027E": "devilfolk",
    "E67DDEDE-EC8D-1AF3-2A0D-24370539F1A6": "devilfolk",
    "6805DD8A-E159-9DAF-648D-D488433EB33F": "devilfolk"
};
const nameFamilyTables: Record<string, [string, string, string]> = {
    "human": [
        "F145078C-472C-6095-8799-923202A8BADC",
        "1919E41C-FB3B-C2FF-E43D-02DD252CF95F",
        "B12A5E42-9FE9-B866-A62E-5E6CC446BEB2"
    ],
    "dwarf": [
        "4377C48B-79CE-0C3C-D104-A390E715AEBA",
        "7280BFDF-8AFE-1853-110C-558A8FF55B6C",
        "C408AC11-75F5-4588-4334-82CF60CB07ED"
    ],
    "elf": [
        "A43F63AA-0B43-14F1-3CAA-9C2E7332EC8D",
        "3BF6186E-59DE-49E3-0EA2-FFB725479C59",
        "75BBDD7E-7308-8EBA-A45E-D35DCF15AEA5"
    ],
    "halfolk": [
        "A186CAB2-E296-8B52-9BD6-5BDA365FA1C7",
        "CBB0DE0F-692D-53BB-FCB2-8254FA68B74C",
        "89EA4523-9897-3D95-014F-876F04506B77"
    ],
    "dragonfolk": [
        "D4E1CE36-292A-AF5A-888C-FE715208CD33",
        "3AEED8B1-9DB8-F187-FABD-EA50D12F3E74",
        "3511FBA6-3C23-3ECF-B574-54789E66EDCD"
    ],
    "gnome": [
        "AE8258F8-B6B9-F247-8CE9-3F4BDD398E15",
        "00E9375A-97CC-DE1D-E118-64D79A91B89E",
        "1460760A-87D4-CFAE-8280-A6FB789C3EB0"
    ],
    "orc": [
        "C9904994-CD5D-7E0B-A4AD-9E321A528B4C",
        "D8D27719-FC09-68AE-E474-D83C0B4DE301",
        ""
    ],
    "devilfolk": [
        "A2687BAC-5387-4436-6557-3B23998B3DCC",
        "6DE0FC3D-8B07-EB97-B600-D3CA096FF6DE",
        "10B8C2A8-FA2A-DEB1-A6E1-66278E6D7C9B"
    ],
    "leprechaun": [
        "44906914-BA0C-6FB0-2B56-5E09960B7799",
        "AE2B59C9-8EED-09AA-35C9-771BB9BB5765",
        "F88DA28B-7F12-D564-9DB7-947C5C4D99DE"
    ]
};
export function creatorNameTables(ancestry: PanelRow, gender: string): { first: string[]; last: string } {
    const nameKeys = ['random_name_table', 'random_name_table_male', 'random_name_table_female', 'random_name_table_last_name'];
    const origin = creatorAncestryPassives(ancestry, 1).find(node => Number(layerOf(node, 'requires_level')) === 1);
    const source = [ancestry, origin].find(node => node && nameKeys.some(key => layerEntriesOf(node, key).length));
    if (source) return creatureNameTables(source, gender);
    const [male, female, last] = nameFamilyTables[ancestryNameFamilies[creatorId(itemsCol(ancestry, 'item_id'))] ?? 'human']!;
    return { first: gender === '0D629F19-23AC-18BE-B79C-73CB52703F66' ? [male] : gender === '1214C099-5021-24B5-5D8D-608B832CE70A' ? [female] : [male, female], last };
}
